var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ButtonNew, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import { GoogleOAuthScope } from 'root/src/shared/constants/google';
import GoogleDriveIcon from 'root/src/client/public/google-drive-icon.svg';
export const GoogleDrivePicker = memo(({ onChange, multiple, isDisabled }) => {
    const { isLoading, handleOpenPicker } = useGoogleDrivePicker({ multiple, onChange });
    return (<ButtonNew variant={ButtonNewVariant.Primary} className="flex items-center gap-2 justify-center w-full" disabled={isLoading || isDisabled} type="button" onClick={handleOpenPicker}>
			{isLoading ? <LoadingCircles width={40} height={40}/> : (<>
					<img width="16" height="16" src={GoogleDriveIcon === null || GoogleDriveIcon === void 0 ? void 0 : GoogleDriveIcon.src} alt="Google Drive Icon"/>
					Upload from Google Drive
				</>)}
		</ButtonNew>);
});
export const useGoogleDrivePicker = ({ multiple, onChange }) => {
    const [pickerInitiated, setPickerInitiated] = useState(false);
    const [gisInitiated, setGisInitiated] = useState(false);
    useEffect(() => {
        const gisScript = document.createElement('script');
        gisScript.src = 'https://accounts.google.com/gsi/client';
        document.body.appendChild(gisScript);
        gisScript.onload = () => {
            setGisInitiated(true);
        };
        const pickerScript = document.createElement('script');
        pickerScript.src = 'https://apis.google.com/js/api.js';
        pickerScript.onload = () => {
            window.gapi.load('picker', () => {
                setPickerInitiated(true);
            });
        };
        document.body.appendChild(pickerScript);
        return () => {
            document.body.removeChild(gisScript);
            document.body.removeChild(pickerScript);
        };
    }, []);
    const handleOpenPicker = () => {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: GCP_CLIENT_ID,
            scope: GoogleOAuthScope.DriveReadonly,
            callback: (response) => __awaiter(void 0, void 0, void 0, function* () {
                if (response.error !== undefined) {
                    toast.error(`Error obtaining access token: ${response.error}`);
                    throw response;
                }
                createPicker(response.access_token);
            }),
        });
        tokenClient.requestAccessToken({ prompt: '' });
    };
    const createPicker = (token) => {
        const docsView = new google.picker.DocsView()
            .setIncludeFolders(true);
        const pickerBuilder = new google.picker.PickerBuilder()
            .setAppId(GCP_PROJECT_NUMBER)
            .setDeveloperKey(GCP_API_KEY)
            .setOAuthToken(token)
            .addView(docsView)
            .addView(new window.google.picker.DocsUploadView())
            .setCallback(data => pickerCallback(data, token));
        if (multiple) {
            pickerBuilder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
        }
        const picker = pickerBuilder.build();
        picker.setVisible(true);
    };
    const pickerCallback = (data, token) => __awaiter(void 0, void 0, void 0, function* () {
        if (data.action === window.google.picker.Action.PICKED) {
            const documents = data[google.picker.Response.DOCUMENTS] || [];
            const taskId = crypto.randomUUID();
            let processed = 0;
            try {
                const files = yield Promise.all(documents.map((document) => __awaiter(void 0, void 0, void 0, function* () {
                    const response = yield fetch(`https://www.googleapis.com/drive/v3/files/${document.id}?alt=media`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    if (!response.ok) {
                        throw new Error(`Failed to download file: ${document.name}`);
                    }
                    const fileBlob = yield response.blob();
                    const fileName = document.name || crypto.randomUUID();
                    processed += 1;
                    toast.loading(`Downloaded ${processed} of ${documents.length} files`, {
                        id: taskId,
                        duration: 0,
                    });
                    return new File([fileBlob], fileName, { type: fileBlob.type });
                })));
                if (!files.length) {
                    toast.error('No files were downloaded', { id: taskId });
                    return;
                }
                toast.success('Google Drive Files uploaded!', { id: taskId, duration: 5000 });
                onChange(files);
            }
            catch (error) {
                console.error(error);
                toast.error(`Error downloading files: ${error.message}`);
            }
        }
    });
    const isLoading = !(pickerInitiated && gisInitiated);
    return { isLoading, handleOpenPicker };
};
